import { useEffect, useState } from "react";
import { formatOutlookQty } from "@/lib/manufacturing/inventoryOutlookFormat";
import { createManufacturingPurchaseOrder, listManufacturingVendors } from "@/lib/manufacturing/purchaseOrderClient";
import type { MfgVendor } from "@/lib/manufacturing/purchaseOrderTypes";
import type { InventoryOutlookReport } from "@/lib/manufacturing/inventoryMatchClient";

const inputClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-600 dark:bg-zinc-900";

function dueText(arrivals: Array<{ date: string | null; qty: number }>): string {
  if (arrivals.length === 0) return "—";
  return arrivals
    .map((row) =>
      row.date ? `Due ${row.date} (${formatOutlookQty(row.qty)})` : `Invoiced, no date (${formatOutlookQty(row.qty)})`,
    )
    .join("; ");
}

function VendorPick({
  vendors,
  value,
  onChange,
}: {
  vendors: MfgVendor[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <select className={inputClass} value={value} aria-label="Vendor" onChange={(e) => onChange(e.target.value)}>
      <option value="">Choose a vendor</option>
      {vendors.map((vendor) => (
        <option key={vendor.id} value={vendor.id}>
          {vendor.name}
        </option>
      ))}
    </select>
  );
}

export function InventoryOutlookPurchase({
  report,
  onDraftCreated,
}: {
  report: InventoryOutlookReport;
  onDraftCreated: (poId: string) => void;
}) {
  const supply = report.supply ?? [];
  const serials = report.serials ?? [];
  const [vendors, setVendors] = useState<MfgVendor[]>([]);
  const [stockVendor, setStockVendor] = useState("");
  const [serialVendor, setSerialVendor] = useState("");
  const [stockOn, setStockOn] = useState<string[]>([]);
  const [serialOn, setSerialOn] = useState<string[]>([]);
  const [stockOpenOnly, setStockOpenOnly] = useState(true);
  const [serialOpenOnly, setSerialOpenOnly] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void listManufacturingVendors({ activeOnly: true }).then((res) => {
      if (res.ok) setVendors(res.data.vendors);
    });
  }, []);

  const stockRows = supply.filter((row) => !stockOpenOnly || row.uncovered > 0);
  const serialRows = serials.filter((row) => !serialOpenOnly || row.uncovered_qty > 0);

  function toggle(list: string[], id: string, set: (next: string[]) => void) {
    set(list.includes(id) ? list.filter((item) => item !== id) : [...list, id]);
  }

  async function draftStock() {
    const chosen = supply.filter((row) => stockOn.includes(row.material_id) && row.uncovered > 0);
    if (!report.plant_id) {
      setError("These buildings are at more than one plant. Draft the purchase order from one plant.");
      return;
    }
    if (!stockVendor || chosen.length === 0) return;
    setBusy(true);
    setError(null);
    const created = await createManufacturingPurchaseOrder({
      plant_id: report.plant_id,
      vendor_id: stockVendor,
      origin: "prediction",
      lines: chosen.map((row) => ({
        material_id: row.material_id,
        material_name: row.material_name || row.material_code || "Material",
        description: "",
        quantity: row.uncovered,
      })),
    });
    setBusy(false);
    if (!created.ok) {
      setError(created.error);
      return;
    }
    setStockOn([]);
    onDraftCreated(created.data.order.id);
  }

  async function draftSerial() {
    const chosen = serials.filter((row) => serialOn.includes(`${row.unit_id}:${row.material_id}`) && row.uncovered_qty > 0);
    const plants = new Set(chosen.map((row) => row.plant_id).filter(Boolean));
    if (plants.size !== 1) {
      setError("Pick buildings from one plant.");
      return;
    }
    if (!serialVendor || chosen.length === 0) return;
    setBusy(true);
    setError(null);
    const created = await createManufacturingPurchaseOrder({
      plant_id: [...plants][0],
      vendor_id: serialVendor,
      origin: "prediction",
      lines: chosen.map((row) => ({
        material_id: row.material_id,
        unit_id: row.unit_id,
        material_name: row.material_name || row.material_code || "Material",
        description: `Serial ${row.serial}`,
        quantity: row.uncovered_qty,
      })),
    });
    setBusy(false);
    if (!created.ok) {
      setError(created.error);
      return;
    }
    setSerialOn([]);
    onDraftCreated(created.data.order.id);
  }

  return (
    <div className="space-y-6">
      {error ? (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {error}
        </p>
      ) : null}
      <section className="space-y-3 rounded-2xl border border-gray-200 p-4 dark:border-zinc-700">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">Supply</h3>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={stockOpenOnly} onChange={(e) => setStockOpenOnly(e.target.checked)} />
            Only what is not on a purchase order
          </label>
        </div>
        {stockRows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-zinc-400">No shortage to order.</p>
        ) : (
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs text-gray-500 dark:text-zinc-400">
                <tr>
                  <th className="py-1 pr-2" />
                  <th className="py-1 pr-2">Code</th>
                  <th className="py-1 pr-2">Material</th>
                  <th className="py-1 pr-2">Short</th>
                  <th className="py-1 pr-2">On hand</th>
                  <th className="py-1 pr-2">Draft</th>
                  <th className="py-1 pr-2">Submitted</th>
                  <th className="py-1 pr-2">Arrival</th>
                  <th className="py-1">Uncovered</th>
                </tr>
              </thead>
              <tbody>
                {stockRows.map((row) => (
                  <tr key={row.material_id} className="border-t border-gray-100 dark:border-zinc-800">
                    <td className="py-1 pr-2">
                      <input
                        type="checkbox"
                        checked={stockOn.includes(row.material_id)}
                        disabled={row.uncovered <= 0}
                        aria-label={`Order ${row.material_code || row.material_name}`}
                        onChange={() => toggle(stockOn, row.material_id, setStockOn)}
                      />
                    </td>
                    <td className="py-1 pr-2">{row.material_code}</td>
                    <td className="py-1 pr-2">{row.material_name}</td>
                    <td className="py-1 pr-2">{formatOutlookQty(row.short)}</td>
                    <td className="py-1 pr-2">{formatOutlookQty(row.on_hand)}</td>
                    <td className="py-1 pr-2">{formatOutlookQty(row.draft_qty)}</td>
                    <td className="py-1 pr-2">{formatOutlookQty(row.submitted_qty)}</td>
                    <td className="py-1 pr-2">{dueText(row.arrivals)}</td>
                    <td className="py-1">{formatOutlookQty(row.uncovered)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <VendorPick vendors={vendors} value={stockVendor} onChange={setStockVendor} />
          <button
            type="button"
            disabled={busy || !stockVendor || stockOn.length === 0}
            className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
            onClick={() => void draftStock()}
          >
            Draft stock purchase order
          </button>
        </div>
      </section>

      <section className="space-y-3 rounded-2xl border border-gray-200 p-4 dark:border-zinc-700">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">Serial coverage</h3>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={serialOpenOnly} onChange={(e) => setSerialOpenOnly(e.target.checked)} />
            Only what is not on a purchase order
          </label>
        </div>
        {serialRows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-zinc-400">No serial on this shortage is waiting for a purchase order.</p>
        ) : (
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs text-gray-500 dark:text-zinc-400">
                <tr>
                  <th className="py-1 pr-2" />
                  <th className="py-1 pr-2">Serial</th>
                  <th className="py-1 pr-2">Material</th>
                  <th className="py-1 pr-2">Need</th>
                  <th className="py-1 pr-2">Uncovered</th>
                  <th className="py-1">Purchase order</th>
                </tr>
              </thead>
              <tbody>
                {serialRows.map((row) => {
                  const key = `${row.unit_id}:${row.material_id}`;
                  return (
                    <tr key={key} className="border-t border-gray-100 dark:border-zinc-800">
                      <td className="py-1 pr-2">
                        <input
                          type="checkbox"
                          checked={serialOn.includes(key)}
                          disabled={row.uncovered_qty <= 0}
                          aria-label={`Order ${row.serial} ${row.material_code || row.material_name}`}
                          onChange={() => toggle(serialOn, key, setSerialOn)}
                        />
                      </td>
                      <td className="py-1 pr-2">{row.serial}</td>
                      <td className="py-1 pr-2">
                        {row.material_code ? `${row.material_code} — ` : ""}
                        {row.material_name}
                      </td>
                      <td className="py-1 pr-2">{formatOutlookQty(row.quantity)}</td>
                      <td className="py-1 pr-2">{formatOutlookQty(row.uncovered_qty)}</td>
                      <td className="py-1">{row.po_numbers.length > 0 ? row.po_numbers.join(", ") : "Not on a purchase order"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <VendorPick vendors={vendors} value={serialVendor} onChange={setSerialVendor} />
          <button
            type="button"
            disabled={busy || !serialVendor || serialOn.length === 0}
            className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
            onClick={() => void draftSerial()}
          >
            Draft serial purchase order
          </button>
        </div>
      </section>
    </div>
  );
}
